import { KeyboardArrowDown } from '@material-ui/icons';
import { Button, Menu, MenuItem } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { setSelectedOrganizationAction } from '@redux/organization/organizationActions';
import { getActiveOrganizationIdSelector, getOrganizationByIdSelector } from '@redux/organization/organizationSelectors';
import { getAllOrganizationsThunk } from '@redux/organization/organizationThunks';
import { Organization } from '@redux/organization/organizationTypes';
import { ReduxState } from '@redux/types';
import { Text } from 'components/Typography';
import { RouteNames } from 'utils/navigationUtils';
import style from './OrganizationSelector.scss';

type PropsFromRedux = ConnectedProps<typeof connectRedux>;

const OrganizationSelector: React.FC<PropsFromRedux> = ({ activeOrganizationId, organizationById, getAllOrganizations, setSelectedOrganization }) => {
  const navigate = useNavigate();
  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorElement);

  useEffect(() => {
    getAllOrganizations();
  }, [getAllOrganizations]);

  const onMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation?.();
    setMenuAnchorElement(event.currentTarget);
  }, []);

  const onMenuClose = useCallback((event: React.MouseEvent) => {
    event.stopPropagation?.();
    setMenuAnchorElement(null);
  }, []);

  const handleOrganizationSelect = useCallback(
    (orgId: string) => (event: React.MouseEvent) => {
      event.stopPropagation?.();
      setSelectedOrganization(orgId);
      setMenuAnchorElement(null);
      // Navigate to AllPatients page to ensure fresh data is loaded
      navigate(RouteNames.ALL_PATIENTS);
    },
    [setSelectedOrganization, navigate]
  );

  const organizations = Object.values(organizationById).sort((a, b) => a.name.localeCompare(b.name));
  const activeOrganization = activeOrganizationId ? organizationById[activeOrganizationId] : null;

  if (organizations.length === 0) {
    return null;
  }

  return (
    <div className={style.container}>
      <Button className={style.button} onClick={onMenuClick} endIcon={<KeyboardArrowDown />}>
        <Text variant="md" className={style.buttonText}>
          {activeOrganization?.name || 'Select Organization'}
        </Text>
      </Button>
      <Menu
        anchorEl={menuAnchorElement}
        open={isMenuOpen}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {organizations.map((org: Organization) => (
          <MenuItem key={org.id} onClick={handleOrganizationSelect(org.id)} selected={org.id === activeOrganizationId} className={style.menuItem}>
            <Text variant="md" className={style.menuItemText}>
              {org.name}
            </Text>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    activeOrganizationId: getActiveOrganizationIdSelector(state),
    organizationById: getOrganizationByIdSelector(state)
  }),
  {
    getAllOrganizations: getAllOrganizationsThunk,
    setSelectedOrganization: setSelectedOrganizationAction
  }
);

export default compose(connectRedux)(OrganizationSelector) as React.ComponentType;
export { OrganizationSelector };
